import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Compass, ArrowRight } from "lucide-react";
import { endpoints } from "@/lib/api";

/**
 * SoulCompassBadge — a compact strip that follows the member's Compass
 * across the app. Rendered on the Home header when a Compass result
 * exists on their profile (mirrored via `profiles.soul_compass` at
 * submit time). Silent when there's no result yet.
 *
 * Renders:
 *   [Compass icon] · The Creator · Freedom  ·  Growth  ·  Creativity  · Open →
 */
export default function SoulCompassBadge({ profile }) {
  const [snap, setSnap] = useState(profile?.soul_compass || null);

  // If the parent didn't pre-load the profile with a soul_compass block,
  // fetch it once so the badge can still render on any surface.
  useEffect(() => {
    if (snap) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await endpoints.profile();
        if (alive && data?.soul_compass) setSnap(data.soul_compass);
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, [snap]);

  if (!snap || !snap.primary_archetype_name) return null;
  const values = Array.isArray(snap.top_values) ? snap.top_values : [];

  return (
    <div
      className="inline-flex flex-wrap items-center gap-2 rounded-full border border-[hsl(var(--border))] bg-white/60 backdrop-blur-sm px-3 py-1.5 text-xs"
      data-testid="soul-compass-badge"
    >
      <span className="inline-flex items-center gap-1.5 text-[hsl(var(--terracotta))]">
        <Compass size={12} />
        <span className="font-display italic text-sm text-[hsl(24,32%,18%)]">
          {snap.primary_archetype_name}
        </span>
      </span>
      {values.length > 0 && (
        <>
          <span className="opacity-40" aria-hidden>·</span>
          <span className="text-[hsl(var(--muted-foreground))] uppercase tracking-[0.18em] text-[10px]">
            {values.join("  ·  ")}
          </span>
        </>
      )}
      {typeof snap.self_connection_pct === "number" && (
        <>
          <span className="opacity-40" aria-hidden>·</span>
          <span
            className="text-[hsl(30,72%,42%)] font-display italic text-sm tabular-nums"
            title="Your self-connection %"
          >
            {snap.self_connection_pct}%
          </span>
        </>
      )}
      <Link
        to="/rituals?pillar=soul"
        className="ml-1 inline-flex items-center gap-1 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))] tab-link"
        data-testid="soul-compass-badge-open"
      >
        <span>Open</span>
        <ArrowRight size={11} />
      </Link>
    </div>
  );
}
